<template>
	<div class="active-product">
		<div class="col-xxs-12 col-xs-5 col-lg-4">
			<div class="active-product__img-container">
				<img
					:src="product.image_url"
					:alt="product.title"
					class="active-product__img img-responsive"
					loading="lazy"
				/>
			</div>
		</div>
		<div class="col-xxs-12 col-xs-7 col-lg-8">
			<div class="row">
				<div class="col-xxs-12">
					<div
						v-html="product.content"
						class="active-product__content"
					></div>
				</div> 
			</div>
			<div class="row">
				<ProductAttributeGroup
					@set_active_attribute="setNewActiveAttribute"
					:id="product.id"
					:attributes="product.attributes"
				/>
				<ProductAddonGroup
					@set_active_addon="setActiveAddon"
					:id="product.id"
					:addons="product.addons"
				/>
			</div>
			<div class="row">
				<div class="col-xxs-12">
					<div class="product__quantity">
						<h4 class="product__quantity__title">Quantity:</h4>
						<div class="product__quantity__input-container flex">
							<button
								@click="handleQtyInput( --product.quantity )"
								:disabled="1 >= product.quantity"
								class="btn btn--black product__quantity__btn product__quantity__btn--subtract"
							>&minus;</button>
							<input
								@change="handleQtyInput( $event.target.value )"	
								:value="product.quantity"
								class="product__quantity__input"
								type="number"
								min="1"
							/>
							<button
								@click="handleQtyInput( ++product.quantity )"
								class="btn btn--black product__quantity__btn product__quantity__btn--add"
							>&plus;</button>
						</div>
					</div>
				</div>
			</div>
			<div class="row">
				<div class="col-xxs-12">
					<button
						@click="handleAddProduct"
						:disabled="isAddingProduct"
						:class="{ 'is-submitting': isAddingProduct }"
						class="btn btn--red active-product__add"
					>{{ submitText }}</button>
				</div>

				<div
					v-if="status.text.length"
					class="col-xxs-12"
				>
					<div
						:class="{ 'is-error': status.isError, 'is-success': status.isSuccess }"
						v-html="status.text"
						class="add-status"
					></div>
				</div>

				<div 
					v-if="addedToCart"
					class="col-xxs-12 push-top soft-top"
				>
					<button 
						@click="$emit( 'go_to_select_items' )"
						class="btn btn--red push-right"
					>Keep Shopping</button>

					<button 
						@click="$emit( 'go_to_cart' )"
						class="btn btn--red"
					>Continue to cart</button>
				</div>


			</div>
		</div>
	</div>
</template>


<script>
	import ProductAttributeGroup from './ProductAttributeGroup.vue'
	import ProductAddonGroup from './ProductAddonGroup.vue'

	import _isEqual from 'lodash.isequal'

	import WpApi from '../helpers/WpApi.mjs'
	import PriceFormatter from '../helpers/PriceFormatter.mjs'


	export default {
		name: 'ActiveProduct',
		components: {
			ProductAttributeGroup,
			ProductAddonGroup,
		},
		
		props: {
			product: {
				type: Object,
				required: true,
			},
		},

		data() {
			return {
				isAddingProduct: 	false,
				addedToCart: 		false,
				isExistingItem: 	null,
				status: 			{
					isError: 	false,
					isSuccess: 	false,
					text: 		''
				}
			}
		},


		computed: {
			submitText() {
				const { cart_item_key = '' } = this.product

				return cart_item_key ? 'Update cart item' : 'Add to cart'
			}
		},


		beforeUpdate() {
			const { cart_item_key = '' } = this.product

			if( cart_item_key.length ) {
				this.isExistingItem = true
			}
		},


		methods: {
			setNewActiveAttribute( attributeKey, optionValue ) {
				const attribute = this.product.attributes[ attributeKey ]

				// value is string; when updated, radio buttons in attribute view will update too
				attribute.active = optionValue

				// update variation ID, which is used when adding product with correct attributes to cart
				this.updateProductVariationId()

				// update price in title in parent step
				this.updateProductPrice( true )
			},


			// @todo — make addon structure more comprehensible
			setActiveAddon( addonKey, optionKey ) {
				const option = this.product.addons[ addonKey ].options[ optionKey ]

				// value is boolean; when updated, checkboxes in addon view will update too
				option.active = !option.active

				// update price in title in parent step
				this.updateProductPrice( true )
			},


			updateProductVariationId() {

				// grab active variation attributes
				const activeAttributes = {}
				for( const attributeName in this.product.attributes ) {
					activeAttributes[ attributeName ] = this.product.attributes[ attributeName ].active
				}

				// loop through variations in active product and find variation that matches
				for( const variationId in this.product.variations ) {
					const variation = this.product.variations[ variationId ],
						variationAttributes = Object.assign( {}, variation.attributes ) // convert from observer to obj

					// does variation match? If so, update price
					if( _isEqual( variationAttributes, activeAttributes ) ) {
						this.product.variation_id	= variation.id
						this.product.price_base 	= variation.price

						break
					}
				}
			},


			updateProductPrice() {

				// grab base price from selected product variation
				let newPrice = this.product.price_base

				// add prices from addons to base price
				for( const addonId in this.product.addons ) {
					const addon = this.product.addons[ addonId ]

					for( const optionId in addon.options ) {
						const option = addon.options[ optionId ]

						if( option.active ) {
							newPrice += option.price
						}
					}
				}

				// update active product with new pricing
				this.product.price = newPrice
				this.product.price_pretty = PriceFormatter.formatToPrice( newPrice )

				// updates title for step
				this.$emit( 'update_active_product_price' )
			},


			handleAddProduct() {
				this.addedToCart = false

				// avoid submitting twice
				if( this.isAddingProduct ) {
					return
				}

				// signal to user process has begun
				this.isAddingProduct = true
				if( this.isExistingItem ) {
					this.status.text = 'Updating product in cart &hellip;'
				} else {
					this.status.text = 'Adding product to cart &hellip;'
				}

				// double check we have an active variation ID
				if( null === this.product.variation_id ) {
					this.updateProductVariationId()
				}

				// are we adding a new cart item or updating an existing cart item?
				const { cart_item_key = '', quantity = 1 } = this.product

				// send product data to WordPress
				WpApi
					.addProductToCart({
						addons: 		this.product.addons,
						cart_item_key: 	cart_item_key,
						product_id: 	this.product.id,
						quantity: 		quantity,
						variation_id:	this.product.variation_id,
					}).then( response => {

						// success will be an object of ALL cart items to send to parent
						if( this.isExistingItem ) {
							this.setStatusSuccess( 'Updated cart!' )

							// go back to cart step
							setTimeout( () => {
								this.$emit( 'finished_updating_cart' )
							}, 1000 )

						} else {
							this.addedToCart = true
							this.setStatusSuccess( 'Added to cart!' )
						}

						this.$emit( 'populate_cart_items', response )

					}).catch( error => {
						this.setStatusError( error )

					}).finally( () => {
						this.isAddingProduct = false
					})
			},


			handleQtyInput( qty ) {
				qty = parseInt( qty )

				if( isNaN( qty ) || 1 > qty ) {
					qty = 1
				}

				this.product.quantity = qty
				this.$forceUpdate()
			},			


			setStatusError( msg = '' ) {
				this.resetStatus()

				if( msg.length ) {
					this.status.isError = true
					this.status.text 	= msg
				}
			},


			setStatusSuccess( msg = '' ) {
				this.resetStatus()

				if( msg.length ) {
					this.status.isSuccess 	= true
					this.status.text 		= msg
				}
			},	
			
			
			resetStatus() {
				if( !this.isAddingProduct ) {
					this.status = {
						isError: 	false,
						isSuccess: 	false,
						text: 		''
					}
				}
			},
		},
	}
</script>